// SearchOrder.js (نسخة الجدول المحسنة مع دفع كام؟)
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { searchOrdersByPhone, updateOrderStatus, revertToNew, deleteOrder } from "../api/OrderAPIs";
import { fetchDesignById } from "../api/DesignAPIs";
import { fetchTypeById } from "../api/TypeAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../api/URL_Header";
import {
  Search,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  Copy,
  Download,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  User,
  PhoneCall,
  Map,
  Hash,
  FileText,
  Trash2,
  Archive,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ShoppingCart,
  RefreshCw,
  Copy as CopyIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Layers,
  MessageSquare,
  Truck,
  Heart,
  Filter,
  MoreVertical,
  Eye,
  EyeOff,
  CreditCard,
  Wallet,
  RotateCcw
} from "lucide-react";

// ==================== Number Input Dialog Component ====================
const NumberInputDialog = ({ isOpen, onClose, onConfirm, title, message }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" dir="rtl">
        <h3 className="text-xl font-bold mb-2 text-gray-800">{title}</h3>
        <p className="text-gray-600 mb-4">{message}</p>
        <input
          ref={inputRef}
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full p-3 border-2 border-gray-300 rounded-xl text-center text-xl font-bold focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
          placeholder="أدخل المبلغ..."
          inputMode="numeric"
          pattern="[0-9]*"
        />
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => {
              onConfirm(value);
              setValue('');
              onClose();
            }}
            className="flex-1 bg-orange-500 text-white py-2 rounded-xl hover:bg-orange-600 transition"
          >
            تأكيد
          </button>
          <button
            onClick={() => {
              setValue('');
              onClose();
            }}
            className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-xl hover:bg-gray-400 transition"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchOrders = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [phoneInput, setPhoneInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  // collapse/expand state for mobile cards
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  
  // States for Number Input Dialog
  const [showNumberDialog, setShowNumberDialog] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [pendingTotal, setPendingTotal] = useState(null);

  const toggleExpand = (orderNumber) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderNumber)) {
        next.delete(orderNumber);
      } else {
        next.add(orderNumber);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
    }
  }, [authState, navigate]);

  const normalizePhoneNumber = (phone) => {
    if (!phone) return phone;
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) cleaned = cleaned.slice(1);
    if (cleaned.startsWith('20') && cleaned.length === 12) cleaned = '0' + cleaned.slice(2);
    if (cleaned.startsWith('2') && cleaned.length === 11) cleaned = '0' + cleaned;
    if (cleaned.length === 10 && /^1[0125]/.test(cleaned)) cleaned = '0' + cleaned;
    return /^01[0125]\d{8}$/.test(cleaned) ? cleaned : phone.replace(/\D/g, '');
  };

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل");
    }
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];
        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;
          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: type.name,
            driveLink: design.code.startsWith("CUSTOM") 
              ? `${API_BASE_URL_V2}${design.driveLink}` 
              : design.driveLink || "",
            code: design.code || "None",
          };
          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }
        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "غير معروف",
          state: order.address?.state || "غير معروف",
          phoneNumber: order.phoneNumber || "غير معروف",
          phoneNumber2: order.phoneNumber2 || "غير معروف",
          name: order.name || "غير معروف",
          note: order.note || "",
          status: order.status || "غير معروف",
        };
      })
    );
  };

  const handleSearch = async () => {
    if (!phoneInput.trim()) {
      toast.error("يرجى إدخال رقم هاتف");
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);
    setExpandedOrders(new Set());
    try {
      const normalizedPhone = normalizePhoneNumber(phoneInput);
      const rawResults = await searchOrdersByPhone(normalizedPhone);
      const enrichedResults = await enrichOrders(rawResults);
      setSearchResults(enrichedResults);
      toast.success("تم البحث بنجاح");
    } catch (err) {
      setSearchError("لا يوجد اوردر بالرقم ده");
    } finally {
      setIsSearching(false);
    }
  };

  const refreshSearch = async () => {
    if (phoneInput.trim()) {
      try {
        const normalizedPhone = normalizePhoneNumber(phoneInput);
        const rawResults = await searchOrdersByPhone(normalizedPhone);
        const enrichedResults = await enrichOrders(rawResults);
        setSearchResults(enrichedResults);
      } catch (err) {
        toast.error("فشل في إعادة تحميل النتائج");
      }
    }
  };

  // ==================== NEW: Handle Partial Payment (دفع كام؟) ====================
  const handlePartialPaymentClick = (orderId, orderTotal) => {
    setPendingOrder(orderId);
    setPendingTotal(orderTotal);
    setShowNumberDialog(true);
  };

  const handleNumberConfirm = async (paidAmount) => {
    if (!paidAmount || paidAmount.trim() === '') {
      toast.error("يجب إدخال المبلغ المدفوع!");
      return;
    }
    
    const paidNumber = parseFloat(paidAmount);
    if (isNaN(paidNumber) || paidNumber < 0) {
      toast.error("يرجى إدخال رقم صحيح للمبلغ المدفوع!");
      return;
    }
    
    const totalAmount = parseFloat(pendingTotal);
    const remainingAmount = totalAmount - paidNumber;
    const paymentNote = `دفع ${paidNumber} و الحساب ${remainingAmount}`;
    
    const confirmMessage = `تأكيد تحديث الحالة إلى "Changes" مع الملاحظة التالية:\n\n"${paymentNote}"\n\nهل أنت متأكد؟`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      await updateOrderStatus(pendingOrder, "Changes", paymentNote);
      await refreshSearch();
      toast.success(`تم تحديث الطلب إلى "تغييرات" مع تسجيل دفعة قدرها ${paidNumber} جنيه!`);
    } catch (err) {
      console.error("Error in handlePartialPayment:", err);
      toast.error("فشل التحديث: " + (err.message || "خطأ غير معروف"));
    }
  };

  // ==================== NEW: Handle Normal Changes (تغييرات عادي) ====================
  const handleNormalChanges = async (orderId) => {
    const reason = prompt("سبب التغيير:");
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("يجب إدخال سبب!");
      return;
    }
    
    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "Changes" مع السبب: "${reason}"؟`);
    if (!confirm) return;
    
    try {
      await updateOrderStatus(orderId, "Changes", reason);
      await refreshSearch();
      toast.success(`تم تحديث الطلب إلى "Changes" بنجاح!`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  // ==================== MODIFIED: handleUpdateStatus (removed Changes case - now handled separately) ====================
  const handleUpdateStatus = async (id, status) => {
    let reason = "no reason";
    
    // Changes is now handled by handleNormalChanges, so we shouldn't get here for Changes
    if (status === "Changes") {
      // This should not happen anymore, but just in case
      handleNormalChanges(id);
      return;
    }
    
    if (status === "Hold") {
      reason = prompt("يرجى إدخال سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب"); return; }
    } else if (status === "Under Purchase") {
      reason = prompt("ادخل الاوردر ريفرنس Girabtcom.xxx:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال الريفيرنس"); return; }
    } else if (status === "Spam") {
      reason = prompt("يرجى إدخال السبب:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب"); return; }
    }
    
    const confirmUpdate = window.confirm(`هل أنت متأكد من تحديث الحالة إلى ${status}؟`);
    if (!confirmUpdate) return;
    
    try {
      await updateOrderStatus(id, status, reason);
      toast.success(`تم تحديث الطلب إلى ${status}`);
      await refreshSearch();
    } catch (err) {
      toast.error("فشل في تحديث الطلب");
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الطلب؟")) return;
    try {
      await deleteOrder(id);
      toast.success("تم حذف الطلب بنجاح");
      await refreshSearch();
    } catch (err) {
      toast.error("فشل في حذف الطلب");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success("تم النسخ بنجاح!");
      }).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success("تم النسخ بنجاح!");
    } catch (err) {
      toast.error("فشل النسخ");
    }
    document.body.removeChild(textArea);
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "غير معروف" ? ` - ${order.phoneNumber2}` : ''}`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${order.state}\t${order.total}\tCOD`;
    copyToClipboard(text);
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      return `${index + 1}-${order.items.length}\t\tPhone Cover (H)\t${item.type}\t\t1\t${item.driveLink || "No Link"}`;
    }).join('\n');
    copyToClipboard(lines);
  };

  const handleCopyAllExcelDataNoReference = async (order) => {
    try {
      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;
      const items = order.items;
      const lines = [];
      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";
        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;
        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });
      copyToClipboard(lines.join('\n'));
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleCopyConfirmationMessage = (order) => {
    const itemsList = order.items.map(item => `Types : ${item.type} - Code : ${item.code}`).join('\n');
    const message = `أهلا بحضرتك" 🌸\nمع حضرتك فاطمة من فريق جرابتكم ستور 💛\nبنراجع مع حضرتك الطلب قبل بدء تنفيذه، لأن التصميمات بتتعمل مخصوص لحضرتك حسب الطلب 🎨✨\n\nتفاصيل الطلب:\n${itemsList}\n\nبمجرد تأكيد حضرتك هنبدأ تنفيذ وتجهيز الأوردر فورًا 🚚⚡\n\nلتأكيد الطلب بشكل نهائي برجاء الرد بــ:\n( نعم - تأكيد ) ✅\n\nوفي حالة الرغبة في التعديل أو الإلغاء برجاء إبلاغنا قبل البدء 💬\nشكرًا لاختيار حضرتك جرابتكم 🤍🌷`;
    copyToClipboard(message);
  };

  const handleCopyBostaMessage = () => {
    const message = `أهلاً وسهلاً!\n\nبنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅\n\nتنويه مهم:\nفي رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  \n\nكمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾\n\nيُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدءالشحن ✉️⚡  \n\nشكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyApologyMessage = () => {
    const message = `أهلاً بحضرتك 🌸\nمع حضرتك فاطمة من جرابتكم ستور 💛\n\nبنعتذر جداً عن التأخير في تنفيذ الطلب الفترة اللي فاتت،\nكان فيه نقص في نوع الجراب المطلوب، لكن الحمد لله تم توفيره الآن وجاهز للتنفيذ ✅\n\nهل نبدأ في تجهيز الطلب فوراً؟ \n\nنقدّر جداً صبرك وذوقك ❤️\nفاطمة - فريق جرابتكم`;
    copyToClipboard(message);
  };

  const getStatusColor = (status) => {
    const colors = {
      "جديد": "bg-blue-100 text-blue-700 border-blue-200",
      "معلق": "bg-yellow-100 text-yellow-700 border-yellow-200",
      "تغييرات": "bg-purple-100 text-purple-700 border-purple-200",
      "تحت الشراء": "bg-orange-100 text-orange-700 border-orange-200",
      "مؤكد": "bg-green-100 text-green-700 border-green-200",
      "قديم": "bg-gray-100 text-gray-700 border-gray-200",
      "ملغي": "bg-red-100 text-red-700 border-red-200",
      "Spam": "bg-red-100 text-red-700 border-red-200",
      "ديبوزت": "bg-indigo-100 text-indigo-700 border-indigo-200",
      "غير معروف": "bg-gray-100 text-gray-700 border-gray-200"
    };
    return colors[status] || "bg-gray-100 text-gray-700 border-gray-200";
  };

  const filteredResults = statusFilter === "all" 
    ? searchResults 
    : searchResults.filter(order => order.status === statusFilter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      
      {/* Number Input Dialog */}
      <NumberInputDialog
        isOpen={showNumberDialog}
        onClose={() => {
          setShowNumberDialog(false);
          setPendingOrder(null);
          setPendingTotal(null);
        }}
        onConfirm={handleNumberConfirm}
        title="دفعة العميل"
        message="أدخل المبلغ الذي دفعه العميل:"
      />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col-2 sm:flex-row justify-between items-center gap-4 mb-3">
          <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            البحث عن الطلبات
          </h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Search Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 md:p-8 mb-2 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="أدخل رقم الهاتف للبحث..."
                className="w-full p-4 pr-14 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white/90"
              />
              <Phone className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400" size={24} />
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg shadow-lg disabled:opacity-70 flex items-center justify-center gap-2"
            >
              <Search size={24} />
              {isSearching ? "جاري البحث..." : "بحث"}
            </button>
          </div>
        </div>

        {/* Results */}
        {isSearching ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8">
            <div className="animate-pulse space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        ) : searchError ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-400" size={64} />
            <p className="text-2xl text-gray-700 mb-2">لا توجد نتائج</p>
            <p className="text-lg text-gray-500">لا يوجد طلبات بهذا الرقم</p>
          </div>
        ) : filteredResults.length > 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
            
            {/* ==================== MOBILE CARDS VIEW ==================== */}
            <div className="flex flex-col gap-5 md:hidden">
              {filteredResults.map((order) => {
                const isExpanded = expandedOrders.has(order.orderNumber);
                return (
                  <div
                    key={order.orderNumber}
                    className="rounded-2xl border border-indigo-100 shadow-md overflow-hidden bg-white"
                  >
                    {/* Note Banner */}
                    {order.note ? (
                      <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-2">
                        <MessageSquare size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <p style={{ fontFamily: "cairo black" }} className="text-[12px] text-amber-800 leading-snug">
                          {order.note}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border-b border-gray-100 px-4 py-2 flex items-center gap-2">
                        <MessageSquare size={14} className="text-gray-300" />
                        <p className="text-sm text-gray-400 italic">لا توجد ملاحظات</p>
                      </div>
                    )}

                    <div className="p-4 space-y-4">
                      
                      {/* Order Number + Status + Date */}
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-gray-400 text-base">#{order.orderNumber}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-800 text-base flex items-center gap-1">
                          <User size={14} className="text-gray-400" />
                          {order.name}
                        </span>
                        <span className="text-sm text-gray-400">{order.date}</span>
                      </div>

                      {/* Phone Numbers - BIG & TAPPABLE */}
                      <div className="space-y-2">
                        <a
                          href={`https://wa.me/${order.phoneNumber.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-2 py-1 text-green-700 active:bg-green-100 transition"
                        >
                          <Phone size={20} className="text-green-600 flex-shrink-0" />
                          <span className="text-lg font-bold tracking-wide" style={{ fontFamily: "monospace" }}>
                            {order.phoneNumber}
                          </span>
                        </a>
                        {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && (
                          <a
                            href={`https://wa.me/${order.phoneNumber2.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-2 py-1 text-green-700 active:bg-green-100 transition"
                          >
                            <Phone size={20} className="text-green-600 flex-shrink-0" />
                            <span className="text-lg font-bold tracking-wide" style={{ fontFamily: "monospace" }}>
                              {order.phoneNumber2}
                            </span>
                          </a>
                        )}
                      </div>

                      {/* Address */}
                      <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                        <div className="flex items-start gap-2">
                          <MapPin size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                          <span style={{ fontFamily: "cairo black" }} className="text-[12px] text-gray-700 leading-snug">
                            {order.address}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Map size={14} className="text-purple-500" />
                          <span className="text-base font-bold text-purple-600">{order.state}</span>
                        </div>
                      </div>
                      
                      
                      {/* NEW: Two buttons row (تغييرات + معلق) */}
                      <div className="grid grid-cols-2 gap-2">
                        <button 
                          onClick={() => handleNormalChanges(order.orderNumber)}
                          className="bg-purple-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <RotateCcw size={14} />
                          تغييرات
                        </button>
                        <button 
                          onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                          className="bg-yellow-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <Clock size={14} />
                          معلق
                        </button>
                      </div>
                      
                      {/* Delete and Spam row */}
                      <div className="grid grid-cols-2 gap-2">
                        <button 
                          onClick={() => handleDeleteOrder(order.orderNumber)}
                          className="bg-red-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <Trash2 size={13} /> 
                          حذف
                        </button>
                        <button 
                          onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                          className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <AlertTriangle size={13} /> 
                          Spam
                        </button>
                      </div>
                      {/* NEW: "دفع كام؟" Button */}
                      <button
                        onClick={() => handlePartialPaymentClick(order.orderNumber, order.total)}
                        className="w-full bg-amber-500 text-white py-1 rounded-xl font-medium flex items-center justify-center gap-2 active:scale-95 transition text-sm"
                      >
                        <CreditCard size={18} />
                        لو دفع ديبوزت , كام؟
                      </button>
                      
                      {/* Total + collapse toggle */}
                      <button
                        onClick={() => toggleExpand(order.orderNumber)}
                        className="w-full flex items-center justify-between bg-green-50 rounded-xl px-3 py-2 border border-green-200 active:bg-green-100 transition"
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded
                            ? <ChevronUp size={20} className="text-green-600" />
                            : <ChevronDown size={20} className="text-green-600" />
                          }
                          <span className="text-gray-500 text-sm">
                            {isExpanded ? "إخفاء التفاصيل" : "المنتجات والإجراءات"}
                          </span>
                        </div>
                        <span className="text-xl font-bold text-green-600">{order.total} ج.م</span>
                      </button>

                      {/* ── Collapsible section ── */}
                      {isExpanded && (
                        <div className="space-y-3">

                          {/* Products */}
                          <div className="space-y-1">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="bg-purple-50 rounded-lg p-2 border border-purple-100">
                                <div className="font-medium text-purple-700 text-xs leading-tight">{item.name}</div>
                                <div style={{ fontFamily: "cairo black" }} className="text-[10px] text-gray-600 leading-tight">{item.type}</div>
                                <div className="flex items-center gap-1 mt-1 flex-wrap">
                                  <span className={`text-[10px] px-1.5 py-[2px] rounded font-mono ${item.code.startsWith("CUSTOM") ? "bg-green-100" : "bg-gray-100"}`}>
                                    {item.code}
                                  </span>
                                  {item.driveLink && (
                                    <div className="flex items-center gap-1">
                                      <a
                                        href={item.driveLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-purple-600 hover:text-purple-800"
                                        title="فتح الرابط"
                                      >
                                        <LinkIcon size={12} />
                                      </a>
                                      {item.code.startsWith("CUSTOM") && (
                                        <button
                                          onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                          className="flex items-center gap-1 text-white px-2 py-[2px] rounded text-[10px]"
                                          style={{ backgroundColor: "#15d225" }}
                                        >
                                          <Download size={10} />
                                          Download
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Copy Buttons */}
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              onClick={() => handleCopyOrderInfo(order)}
                              className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition"
                            >
                              <CopyIcon size={12} />
                              العميل
                            </button>
                            <button
                              onClick={() => handleCopyItems(order)}
                              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition"
                            >
                              <Layers size={12} />
                              المنتجات
                            </button>
                            <button
                              onClick={() => handleCopyAllExcelDataNoReference(order)}
                              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition"
                            >
                              <Copy size={12} />
                              نسخ الكل
                            </button>
                          </div>

                          {/* More Action Buttons */}
                          <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                              className="bg-green-600 text-white py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-1 active:scale-95 transition">
                              <CheckCircle size={12} /> تأكيد
                            </button>
                            <button onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                              className="bg-orange-600 text-white py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-1 active:scale-95 transition">
                              <ShoppingCart size={12} /> تحت الشراء
                            </button>
                            <button onClick={() => handleUpdateStatus(order.orderNumber, "New")}
                              className="bg-blue-600 text-white py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-1 active:scale-95 transition">
                              <RefreshCw size={12} /> إعادة لجديد
                            </button>
                            <button onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                              className="bg-gray-500 text-white py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-1 active:scale-95 transition">
                              <Archive size={12} /> قديم
                            </button>
                            <button onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                              className="col-span-2 bg-indigo-600 text-white py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-1 active:scale-95 transition">
                              <DollarSign size={12} /> ديبوزت
                            </button>
                          </div>
                        </div>
                      )}
                      {/* ── End collapsible ── */}

                    </div>
                  </div>
                );
              })}
            </div>
            {/* ==================== END MOBILE CARDS ==================== */}

            {/* Desktop Table */}
            <table className="w-full min-w-[1200px] table-auto hidden md:table">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                  <th className="p-4 text-right font-bold text-indigo-800">بيانات العميل</th>
                  <th className="p-4 text-right font-bold text-indigo-800">المنتجات</th>
                  <th className="p-4 text-center font-bold text-indigo-800">الحالة</th>
                  <th className="p-4 text-right font-bold text-indigo-800">ملاحظات</th>
                  <th className="p-4 text-center font-bold text-indigo-800">نسخ</th>
                  <th className="p-4 text-center font-bold text-indigo-800">إجراءات</th>
                  <th className="p-4 text-center font-bold text-indigo-800">حذف</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((order) => (
                  <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                    <td className="p-4 align-top">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-indigo-600" />
                          <span style={{fontFamily: "cairo black"}} className="text-sm">{order.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone size={16} className="text-green-600" />
                          <div className="flex flex-col">
                            <span onClick={() => navigator.clipboard.writeText(order.phoneNumber)}
                              className="text-green-600 hover:underline cursor-pointer">{order.phoneNumber}</span>
                            {order.phoneNumber2 !== "غير معروف" && (
                              <span onClick={() => navigator.clipboard.writeText(order.phoneNumber2)}
                                className="text-green-600 hover:underline cursor-pointer">{order.phoneNumber2}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-purple-600" />
                          <span style={{fontFamily: "cairo black"}} className="text-[13px]">{order.address}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Map size={16} className="text-orange-600" />
                          <span>{order.state}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign size={16} className="text-green-600" />
                          <span style={{fontFamily: "cairo black"}} className="text-2xl font-bold text-green-600">{order.total} ج.م</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-gray-600" />
                          <span className="text-lg">{order.date}</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {order.items.map((item, index) => (
                        <div key={`${item.id}-${index}`} className="mb-3">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="font-medium text-purple-700">{item.name}</div>
                              <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                              <div className="flex items-center gap-2 mt-1">
                                {item.code.startsWith("CUSTOM") && (
                                  <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                )}
                                {!item.code.startsWith("CUSTOM") && (
                                  <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                )}
                                {item.driveLink && (
                                  <div className="flex items-center gap-1">
                                    <a href={item.driveLink} target="_blank" rel="noopener noreferrer"
                                      className="text-purple-600 hover:text-purple-800 transition-transform hover:scale-110" title="فتح الرابط">
                                      <LinkIcon size={16} />
                                    </a>
                                    {item.code.startsWith("CUSTOM") && (
                                      <button onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                        className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                        title="تحميل الصورة" style={{ backgroundColor: "#15d225", borderRadius: "4px" }}>
                                        <Download size={16} />
                                        <span className="text-sm">Download</span>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {index < order.items.length - 1 && <hr className="my-2 border-gray-200" />}
                        </div>
                      ))}
                    </td>
                    <td className="p-4 text-center align-top">
                      <span className={`inline-block px-3 py-1 rounded-full text-lg font-medium border ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="p-4 align-top max-w-[150px]">
                      <p style={{fontFamily:"cairo black"}} className="text-[16px]">{order.note || "—"}</p>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-col gap-2">
                        <button onClick={() => handleCopyOrderInfo(order)}
                          className="flex items-center justify-center gap-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-lg hover:shadow-md hover:scale-105 transition text-sm">
                          <CopyIcon size={16} /> العميل
                        </button>
                        <button onClick={() => handleCopyItems(order)}
                          className="flex items-center justify-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-md hover:scale-105 transition text-sm">
                          <Layers size={16} /> المنتجات
                        </button>
                        <button onClick={() => handleCopyAllExcelDataNoReference(order)}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2">
                          <Copy size={16} /> نسخ
                        </button>
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-col gap-2 min-w-[120px]">
                        {/* NEW: "دفع كام؟" Button */}
                        <button 
                          onClick={() => handlePartialPaymentClick(order.orderNumber, order.total)}
                          className="flex items-center justify-center gap-1 bg-amber-500 text-white px-3 py-2 rounded-lg hover:bg-amber-600 text-sm font-medium"
                        >
                          <CreditCard size={14} /> دفع كام؟
                        </button>
                        {/* NEW: "تغييرات" Button (replaces old Changes) */}
                        <button 
                          onClick={() => handleNormalChanges(order.orderNumber)}
                          className="flex items-center justify-center gap-1 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 text-sm font-medium"
                        >
                          <RotateCcw size={14} /> تغييرات
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                          className="flex items-center justify-center gap-1 bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 text-sm">
                          <Archive size={14} /> قديم
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                          className="flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm">
                          <CheckCircle size={14} /> تأكيد
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                          className="flex items-center justify-center gap-1 bg-yellow-600 text-white px-3 py-2 rounded-lg hover:bg-yellow-700 text-sm">
                          <Clock size={14} /> معلق
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                          className="flex items-center justify-center gap-1 bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 text-sm">
                          <ShoppingCart size={14} /> تحت الشراء
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "New")}
                          className="flex items-center justify-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm">
                          <RefreshCw size={14} /> إعادة لجديد
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                          className="flex items-center justify-center gap-1 bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 text-sm">
                          <DollarSign size={14} /> ديبوزت
                        </button>
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-col gap-2">
                        <button onClick={() => handleDeleteOrder(order.orderNumber)}
                          className="flex items-center justify-center gap-1 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 text-sm">
                          <Trash2 size={14} /> حذف
                        </button>
                        <button onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                          className="flex items-center justify-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm">
                          <AlertTriangle size={14} /> Spam
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          </div>
        ) : phoneInput && !isSearching ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <Package className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات</p>
            <p className="text-lg text-gray-500">لم يتم العثور على طلبات بهذا الرقم</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchOrders;